import React, { useState, useEffect, useRef, useMemo } from 'react';
import html2canvas from 'html2canvas';
import * as htmlToImage from 'html-to-image';
import ParticipantTile from './ParticipantTile';
import Controls from './Controls';
import AnimatedBottomSheet from '../common/animatedBottomSheet';
import SettingsPanel from './SettingsPanel';
import { requestScreenWakeLock, releaseScreenWakeLock } from './screenWakeLock';
import { getPharmacyCurrentDateTimeParts } from '../../utils/helper/helper';
import {
  RELIABLE_VIDEO_PUBLISHER_OPTIONS,
  applyPublisherNetworkProfile,
} from '../../utils/vonagePublisherQuality';

const isVonageOrWebRTCError = (message) =>
  typeof message === 'string' &&
  /getStats|MediaStreamTrack|localDescription|RTCPeerConnection|OpenTok|SessionDispatcher|opentok|LiveKit|livekit|SignalClient|ConnectionError|PublishTrackError|TrackInvalidError|UnexpectedConnectionState|audio level|getUserMedia|NotReadableError|NotAllowedError|NotFoundError|devices are unavailable|selected voice or video devices|camera or microphone|audio or video/i.test(
    message
  );

const isCameraPermissionDenied = (error) => {
  const message = `${error?.name || ''} ${error?.message || error || ''}`;
  return /NotAllowedError|OT_USER_MEDIA_ACCESS_DENIED|permission.*denied|access.*denied/i.test(message);
};

const installGetStatsCompatibilityShim = () => {
  if (typeof window === 'undefined') return;
  const peerConnectionPrototype = window.RTCPeerConnection?.prototype;
  if (!peerConnectionPrototype?.getStats || peerConnectionPrototype.getStats.__oatrxCompatible) return;

  const nativeGetStats = peerConnectionPrototype.getStats;
  const isMediaStreamTrack = (value) =>
    typeof window.MediaStreamTrack !== 'undefined' &&
    value instanceof window.MediaStreamTrack;

  peerConnectionPrototype.getStats = function getStatsCompat(selectorOrCallback, legacyCallback) {
    if (typeof selectorOrCallback === 'function') {
      nativeGetStats.call(this)
        .then((stats) => selectorOrCallback(stats))
        .catch((error) => {
          if (typeof legacyCallback === 'function') legacyCallback(error);
        });
      return undefined;
    }

    if (typeof legacyCallback === 'function') {
      const selector =
        isMediaStreamTrack(selectorOrCallback)
          ? selectorOrCallback
          : undefined;

      nativeGetStats.call(this, selector)
        .then((stats) => legacyCallback(null, stats))
        .catch((error) => legacyCallback(error));
      return undefined;
    }

    if (
      selectorOrCallback != null &&
      !isMediaStreamTrack(selectorOrCallback)
    ) {
      return nativeGetStats.call(this);
    }

    return nativeGetStats.call(this, selectorOrCallback);
  };

  peerConnectionPrototype.getStats.__oatrxCompatible = true;
};

// Parse OpenTok session ID format "2_MX<api_key>-<session_id>-..." to get apiKey for initSession
const parseVonageSessionId = (sessionIdStr) => {
  if (!sessionIdStr || typeof sessionIdStr !== 'string') return { apiKey: null, sessionId: sessionIdStr };
  const normalized = String(sessionIdStr).trim().replace(/\s+/g, '');
  const parts = normalized.split('-');
  if (parts[0] && parts[0].startsWith('2_MX')) {
    return { apiKey: parts[0].substring(4), sessionId: normalized };
  }
  return { apiKey: null, sessionId: normalized };
};

// Normalize Vonage token for OpenTok SDK (trim + remove any stray whitespace/newlines)
const normalizeVonageToken = (token) => {
  if (token == null) return '';
  const s = String(token).trim();
  return s.replace(/\s+/g, '');
};

const VideoCall = ({
  isOpen,
  onClose,
  patientData,
  driverName,
  pharmacistName = 'Pharmacist',
  callState = 'accepted', // 'idle', 'requesting', 'accepted', 'declined', 'ended'
  statusReason = null, // optional: 'busy' | 'timeout' | null
  pharmacistBusy = false, // true while the assigned pharmacist is on another call (request stays queued)
  isCaller = true, // true if user initiated the call, false if user received the call
  vonageSessionId,
  vonageToken,
  applicationId, // API key from backend (application_id from initiate-video-call API)
  // Provider is pinned per call by the backend. "vonage" keeps the original
  // OpenTok path; "meditalk" uses LiveKit, where vonageSessionId carries the
  // room name and vonageToken carries the LiveKit JWT (backend reuses the
  // fields deliberately, and leaves application_id empty for MediTalk).
  videoProvider = 'vonage',
  livekitUrl = '',
  onScreenshot, // optional: (blob: Blob) => void — called with PNG blob when user takes a screenshot (e.g. for future API upload)
  autoScreenshot = false, // when true (pharmacist side only): first screenshot after 5s, then every 5s, saved to API only
  callMode = 'delivery_to_pharmacist'
}) => {
  const isMeditalkProvider =
    String(videoProvider || '').trim().toLowerCase() === 'meditalk';
  const { apiKey: vonageApiKeyFromSessionId, sessionId: vonageSessionIdParsed } = useMemo(
    () => parseVonageSessionId(vonageSessionId),
    [vonageSessionId]
  );
  const vonageApiKey = useMemo(
    () => (applicationId != null && String(applicationId).trim()) ? String(applicationId).trim().replace(/\s+/g, '') : vonageApiKeyFromSessionId,
    [applicationId, vonageApiKeyFromSessionId]
  );
  const vonageTokenNormalized = useMemo(
    () => normalizeVonageToken(vonageToken),
    [vonageToken]
  );
  const isCallJoinable =
    isOpen && (callState === 'accepted' || (isCaller && callState === 'requesting'));
  const shouldPrepareVonage = !!(
    isOpen &&
    !isMeditalkProvider &&
    vonageApiKey &&
    vonageSessionIdParsed &&
    vonageTokenNormalized &&
    (callState === 'accepted' || (isCaller && callState === 'requesting'))
  );
  const useVonage = shouldPrepareVonage;

  // MediTalk/LiveKit: room name arrives in vonageSessionId, JWT in vonageToken,
  // and the websocket URL in livekitUrl. application_id is intentionally empty.
  const livekitRoomName = useMemo(
    () => (vonageSessionId == null ? '' : String(vonageSessionId).trim()),
    [vonageSessionId]
  );
  const livekitServerUrl = useMemo(
    () => (livekitUrl == null ? '' : String(livekitUrl).trim()),
    [livekitUrl]
  );
  const useLiveKit = !!(
    isCallJoinable &&
    isMeditalkProvider &&
    livekitServerUrl &&
    livekitRoomName &&
    vonageTokenNormalized
  );
  // Either provider renders into the same SDK-owned containers.
  const useSdkContainers = useVonage || useLiveKit;
  // Media capture role: drivers get maintain-framerate (they are moving),
  // pharmacists maintain-resolution.
  const mediaRole =
    callMode === 'pharmacist_to_driver' ? 'pharmacist' : (isCaller ? 'driver' : 'pharmacist');

  const [isMicOn, setIsMicOn] = useState(true);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [cameraPermissionState, setCameraPermissionState] = useState('unknown');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [microphones, setMicrophones] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [selectedMicId, setSelectedMicId] = useState('');
  const [selectedCameraId, setSelectedCameraId] = useState('');
  const [pipPosition, setPipPosition] = useState({ top: '1rem', right: '1rem' });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [callDateTime, setCallDateTime] = useState(() => getPharmacyCurrentDateTimeParts());
  const [screenshotToastMessage, setScreenshotToastMessage] = useState(null); // null | 'manual' | 'auto'
  // LiveKit only: drivers move through poor coverage constantly, so a reconnect
  // must be visible rather than looking like a frozen call.
  const [liveKitStatus, setLiveKitStatus] = useState('idle'); // 'idle' | 'connecting' | 'connected' | 'reconnecting'
  const screenshotFeedbackTimeoutRef = useRef(null);
  const pipContainerRef = useRef(null);
  const containerRef = useRef(null);

  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const localVideoRef = useRef(null);
  const vonageSessionRef = useRef(null);
  const vonagePublisherRef = useRef(null);
  const vonageSubscriberRef = useRef(null);
  const livekitSessionRef = useRef(null);
  const appliedCameraIdRef = useRef(null);
  const appliedMicIdRef = useRef(null);
  const publisherContainerRef = useRef(null);
  const subscriberContainerRef = useRef(null);
  const screenshotAreaRef = useRef(null);
  const cameraPermissionDeniedRef = useRef(false);
  const reportCameraPermissionDeniedRef = useRef(null);
  const onScreenshotRef = useRef(onScreenshot);
  onScreenshotRef.current = onScreenshot;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Clear screenshot feedback timeout on unmount
  useEffect(() => {
    return () => {
      if (screenshotFeedbackTimeoutRef.current) {
        clearTimeout(screenshotFeedbackTimeoutRef.current);
        screenshotFeedbackTimeoutRef.current = null;
      }
    };
  }, []);

  // Initialize media devices. When Vonage is available, start it during the caller's
  // requesting state so the other participant does not wait for media after accept.
  useEffect(() => {
    if (isOpen) {
      initializeMediaDevices();
      if (!useVonage && !useLiveKit && callState === 'accepted') {
        startLocalStream();
      } else {
        stopLocalStream();
      }
    } else {
      stopLocalStream();
    }

    return () => {
      stopLocalStream();
    };
  }, [isOpen, callState, selectedCameraId, selectedMicId, useVonage, useLiveKit]);

  // Check early so a driver with a browser-blocked camera sees the issue before
  // the call is accepted. Some browsers do not support this API, so the normal
  // publisher error handling remains the final check.
  useEffect(() => {
    if (!isOpen) {
      setCameraPermissionState('unknown');
      return undefined;
    }
    if (!navigator.permissions?.query) return undefined;

    let permissionStatus = null;
    let cancelled = false;
    const checkCameraPermission = async () => {
      try {
        permissionStatus = await navigator.permissions.query({ name: 'camera' });
        if (cancelled) return;
        const updatePermissionState = () => {
          const isDenied = permissionStatus.state === 'denied';
          cameraPermissionDeniedRef.current = isDenied;
          setCameraPermissionState(permissionStatus.state);
          if (isDenied) reportCameraPermissionDeniedRef.current?.();
        };
        updatePermissionState();
        permissionStatus.onchange = updatePermissionState;
      } catch (error) {
        // Browser does not expose camera permission status. The call will still
        // report an actual denial when it requests the camera.
      }
    };

    checkCameraPermission();
    return () => {
      cancelled = true;
      if (permissionStatus) permissionStatus.onchange = null;
    };
  }, [isOpen]);

  // Keep date/time ticking (same as top bar) so it doesn't appear paused when call starts
  useEffect(() => {
    if (!isOpen) return;
    const tick = () => setCallDateTime(getPharmacyCurrentDateTimeParts());
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [isOpen]);

  // Join Vonage session – SDK requires DOM targets; start publisher and session connect
  // in parallel so remote video can appear sooner after pharmacist acceptance.
  useEffect(() => {
    if (!useVonage) return;

    let session = null;
    let cancelled = false;

    let canPublishToSession = true;
    let preparedPublisher = null;

    let remoteEndNotified = false;

    let lastLoggedAt = 0;
    const ERROR_LOG_DEBOUNCE_MS = 5000;

    const reportCameraPermissionDenied = () => {
      if (cancelled || !cameraPermissionDeniedRef.current || !session?.connection) return;
      try {
        session.signal({ type: 'cameraPermissionDenied' }, () => {});
      } catch (signalError) {
        // The Portal can still learn about the denial from the next connection event.
      }
    };
    reportCameraPermissionDeniedRef.current = reportCameraPermissionDenied;

    const logVideoErrorOnceInAWhile = () => {
      const now = Date.now();
      if (now - lastLoggedAt < ERROR_LOG_DEBOUNCE_MS) return;
      lastLoggedAt = now;
      // Keep a console log for debugging but avoid user-facing toasts.
      // eslint-disable-next-line no-console
      console.warn('Video connection had a temporary issue. You can continue or end the call.');
    };

    const clearPublisherContainer = () => {
      if (publisherContainerRef.current) {
        publisherContainerRef.current.innerHTML = '';
      }
    };

    const onError = (event) => {
      const msg = event?.message || event?.reason?.message || String(event?.reason || '');
      if (isVonageOrWebRTCError(msg)) {
        logVideoErrorOnceInAWhile();
        event?.preventDefault?.();
        event?.stopPropagation?.();
        return true;
      }
      return false;
    };
    const onUnhandledRejection = (event) => {
      const msg = event?.reason?.message || String(event?.reason || '');
      if (isVonageOrWebRTCError(msg)) {
        logVideoErrorOnceInAWhile();
        event.preventDefault();
        event.stopPropagation();
      }
    };

    window.addEventListener('error', onError, true);
    window.addEventListener('unhandledrejection', onUnhandledRejection, true);

    try {
      installGetStatsCompatibilityShim();
      const OT = require('@vonage/client-sdk-video');
      session = OT.initSession(vonageApiKey, vonageSessionIdParsed);
      vonageSessionRef.current = session;

      session.on('error', (err) => {
        if (!cancelled) logVideoErrorOnceInAWhile();
      });

      session.on('connectionCreated', (event) => {
        if (cancelled || event?.connection?.connectionId === session.connection?.connectionId) return;
        reportCameraPermissionDenied();
      });

      session.on('streamCreated', (event) => {
        if (cancelled || !event.stream) return;
        try {
          const subEl = subscriberContainerRef.current;
          if (subEl) {
            const subscriber = session.subscribe(event.stream, subEl, {
              insertMode: 'append',
              width: '100%',
              height: '100%',
              fitMode: 'cover',
              showControls: false // hide Vonage default speaker/volume UI
            });
            vonageSubscriberRef.current = subscriber;

            // Make a black remote diagnosable: the OpenTok media server suppresses
            // the remote video when the other side's connection is weak (reason
            // "quality") or they turn their camera off ("publishVideo"), which is
            // the "remote video is black / takes forever to load" symptom. This app
            // intentionally avoids user-facing toasts, so we only log the state
            // transitions (throttled elsewhere) — video re-enables automatically.
            if (subscriber && typeof subscriber.on === 'function') {
              subscriber.on('videoDisabled', (e) => {
                if (!cancelled) console.info('[video-call] remote video disabled:', e?.reason);
              });
              subscriber.on('videoEnabled', (e) => {
                if (!cancelled) console.info('[video-call] remote video enabled:', e?.reason);
              });
            }
          }
        } catch (e) {
          if (!cancelled) logVideoErrorOnceInAWhile();
        }
      });

      session.on('streamDestroyed', () => {
        vonageSubscriberRef.current = null;
        // Remote participant left (e.g. pharmacy tab closed / hard reload). Session-level
        // streamDestroyed is only fired for streams from other connections (OpenTok SDK).
        if (cancelled || remoteEndNotified) return;
        remoteEndNotified = true;
        try {
          onCloseRef.current?.();
        } catch (_) {
          /* ignore */
        }
      });

      const preparePublisher = ({ publishAudio = isMicOn, publishVideo = isVideoOn } = {}) => {
        const pubEl = publisherContainerRef.current;
        if (!pubEl) return Promise.resolve(null);

        return new Promise((resolve, reject) => {
          clearPublisherContainer();
          const publisher = OT.initPublisher(pubEl, {
            insertMode: 'append',
            width: '100%',
            height: '100%',
            fitMode: 'cover',
            showControls: false,
            ...RELIABLE_VIDEO_PUBLISHER_OPTIONS,
            publishAudio,
            publishVideo
          }, (err) => {
            if (err) {
              try {
                publisher?.destroy?.();
              } catch (destroyError) {}
              clearPublisherContainer();
              reject(err);
              return;
            }

            preparedPublisher = publisher;
            applyPublisherNetworkProfile(publisher);
            if (cancelled || !canPublishToSession) {
              try {
                publisher.destroy();
              } catch (destroyError) {}
              preparedPublisher = null;
              resolve(null);
              return;
            }

            resolve(publisher);
          });
        });
      };

      const preparedPublisherPromise = preparePublisher()
        .catch((firstError) => {
          if (cancelled) throw firstError;
          logVideoErrorOnceInAWhile();
          return preparePublisher({ publishAudio: isMicOn, publishVideo: isVideoOn });
        });

      // Prevent a fast publisher failure from surfacing as an unhandled runtime error
      // before session.connect has a chance to attach its normal handling below.
      preparedPublisherPromise.catch(() => {});

      session.connect(vonageTokenNormalized, (err) => {
        if (cancelled) return;
        if (err) {
          canPublishToSession = false;
          if (preparedPublisher) {
            try {
              preparedPublisher.destroy();
            } catch (destroyError) {}
            preparedPublisher = null;
          }
          logVideoErrorOnceInAWhile();
          return;
        }

        reportCameraPermissionDenied();

        preparedPublisherPromise
          .then((publisher) => {
            if (!publisher || cancelled || !canPublishToSession) return;

            session.publish(publisher, (publishErr) => {
              if (publishErr && !cancelled) {
                logVideoErrorOnceInAWhile();
                return;
              }

              if (cancelled) {
                try {
                  publisher.destroy();
                } catch (destroyError) {}
                return;
              }

              vonagePublisherRef.current = publisher;
              preparedPublisher = publisher;

              if (typeof window !== 'undefined' && window.VideoCallBridge) {
                try { window.VideoCallBridge.postMessage('videoCallStarted'); } catch (_) {}
              }
            });
          })
          .catch((publishError) => {
            if (!cancelled) {
              if (isCameraPermissionDenied(publishError)) {
                cameraPermissionDeniedRef.current = true;
                reportCameraPermissionDenied();
              }
              logVideoErrorOnceInAWhile();
            }
          });
      });

      return () => {
        cancelled = true;
        if (reportCameraPermissionDeniedRef.current === reportCameraPermissionDenied) {
          reportCameraPermissionDeniedRef.current = null;
        }
        window.removeEventListener('error', onError, true);
        window.removeEventListener('unhandledrejection', onUnhandledRejection, true);
        if (session) {
          try {
            const publishedPublisher = vonagePublisherRef.current;
            if (publishedPublisher) {
              session.unpublish(publishedPublisher);
              vonagePublisherRef.current = null;
            }
            if (preparedPublisher && preparedPublisher !== publishedPublisher) {
              preparedPublisher.destroy();
              preparedPublisher = null;
            }
            if (vonageSubscriberRef.current) {
              session.unsubscribe(vonageSubscriberRef.current);
              vonageSubscriberRef.current = null;
            }
            session.disconnect();
          } catch (e) {
            console.warn('Vonage cleanup error:', e);
          }
          vonageSessionRef.current = null;
        }
      };
    } catch (e) {
      window.removeEventListener('error', onError, true);
      window.removeEventListener('unhandledrejection', onUnhandledRejection, true);
      logVideoErrorOnceInAWhile();
      return () => {};
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isMicOn/isVideoOn intentionally omitted to avoid reconnecting on toggle
  }, [useVonage, vonageApiKey, vonageSessionIdParsed, vonageTokenNormalized]);

  // Join the MediTalk (LiveKit) room. This effect and the Vonage one above are
  // mutually exclusive: useVonage is false for meditalk calls and useLiveKit is
  // false for vonage calls, so only one SDK is ever loaded or connected.
  useEffect(() => {
    if (!useLiveKit) return undefined;

    let cancelled = false;
    let session = null;
    let remoteEndNotified = false;
    let lastLoggedAt = 0;
    const ERROR_LOG_DEBOUNCE_MS = 5000;

    const logVideoErrorOnceInAWhile = () => {
      const now = Date.now();
      if (now - lastLoggedAt < ERROR_LOG_DEBOUNCE_MS) return;
      lastLoggedAt = now;
      // eslint-disable-next-line no-console
      console.warn('[video-call] MediTalk video had a temporary issue. You can continue or end the call.');
    };

    const onError = (event) => {
      const msg = event?.message || event?.reason?.message || String(event?.reason || '');
      if (isVonageOrWebRTCError(msg)) {
        logVideoErrorOnceInAWhile();
        event?.preventDefault?.();
        event?.stopPropagation?.();
        return true;
      }
      return false;
    };
    const onUnhandledRejection = (event) => {
      const msg = event?.reason?.message || String(event?.reason || '');
      if (isVonageOrWebRTCError(msg)) {
        logVideoErrorOnceInAWhile();
        event.preventDefault();
        event.stopPropagation();
      }
    };

    window.addEventListener('error', onError, true);
    window.addEventListener('unhandledrejection', onUnhandledRejection, true);

    setLiveKitStatus('connecting');

    try {
      // Lazily required, like the Vonage SDK above, so a vonage-only call never
      // evaluates the LiveKit client.
      const { createLiveKitSession } = require('./livekitSession');
      session = createLiveKitSession({
        roomName: livekitRoomName,
        url: livekitServerUrl,
        role: mediaRole,
      });
      livekitSessionRef.current = session;

      session.on('error', () => {
        if (!cancelled) logVideoErrorOnceInAWhile();
      });

      // Poor coverage is normal for a driver: show it, do not end the call.
      session.on('reconnecting', () => {
        if (!cancelled) setLiveKitStatus('reconnecting');
      });
      session.on('reconnected', () => {
        if (!cancelled) setLiveKitStatus('connected');
      });

      session.on('videoDisabled', () => {
        if (!cancelled) console.info('[video-call] remote video disabled (meditalk)');
      });
      session.on('videoEnabled', () => {
        if (!cancelled) console.info('[video-call] remote video enabled (meditalk)');
      });

      session.on('videoUnavailable', (error) => {
        if (cancelled) return;
        if (isCameraPermissionDenied(error)) {
          cameraPermissionDeniedRef.current = true;
          setCameraPermissionState('denied');
        }
      });

      // Only fires after the adapter's grace period with nobody left in the
      // room — a single ParticipantDisconnected during a reconnect does not.
      session.on('remoteLeft', () => {
        if (cancelled || remoteEndNotified) return;
        remoteEndNotified = true;
        try {
          onCloseRef.current?.();
        } catch (closeError) {
          /* ignore */
        }
      });

      session.setLocalContainer(publisherContainerRef.current);
      session.setRemoteContainer(subscriberContainerRef.current);

      session
        .connect(vonageTokenNormalized)
        .then(async () => {
          if (cancelled) return;
          setLiveKitStatus('connected');
          // Containers may have mounted between construction and connect.
          session.setLocalContainer(publisherContainerRef.current);
          session.setRemoteContainer(subscriberContainerRef.current);

          await session.publish({
            audioDeviceId: selectedMicId,
            videoDeviceId: selectedCameraId,
            publishAudio: isMicOn,
            publishVideo: isVideoOn,
          });
          if (cancelled) return;

          if (typeof window !== 'undefined' && window.VideoCallBridge) {
            try { window.VideoCallBridge.postMessage('videoCallStarted'); } catch (_) {}
          }
        })
        .catch((error) => {
          if (cancelled) return;
          if (isCameraPermissionDenied(error)) {
            cameraPermissionDeniedRef.current = true;
            setCameraPermissionState('denied');
          }
          logVideoErrorOnceInAWhile();
        });
    } catch (error) {
      logVideoErrorOnceInAWhile();
    }

    return () => {
      cancelled = true;
      window.removeEventListener('error', onError, true);
      window.removeEventListener('unhandledrejection', onUnhandledRejection, true);
      try {
        session?.disconnect();
      } catch (error) {
        console.warn('LiveKit cleanup error:', error);
      }
      livekitSessionRef.current = null;
      setLiveKitStatus('idle');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isMicOn/isVideoOn/device ids intentionally omitted to avoid rejoining the room on toggle
  }, [useLiveKit, livekitRoomName, livekitServerUrl, vonageTokenNormalized, mediaRole]);

  // Device picker changes during a live MediTalk call: restart the published
  // track in place instead of rejoining the room. The first run only records
  // the device the join already published with.
  useEffect(() => {
    if (!useLiveKit || liveKitStatus !== 'connected') {
      appliedCameraIdRef.current = null;
      appliedMicIdRef.current = null;
      return;
    }
    const session = livekitSessionRef.current;
    if (!session) return;

    if (appliedCameraIdRef.current === null) {
      appliedCameraIdRef.current = selectedCameraId || '';
    } else if (appliedCameraIdRef.current !== (selectedCameraId || '')) {
      appliedCameraIdRef.current = selectedCameraId || '';
      Promise.resolve(session.setVideoSource(selectedCameraId)).catch((error) => {
        console.warn('[video-call] unable to switch MediTalk camera device', error);
      });
    }

    if (appliedMicIdRef.current === null) {
      appliedMicIdRef.current = selectedMicId || '';
    } else if (appliedMicIdRef.current !== (selectedMicId || '')) {
      appliedMicIdRef.current = selectedMicId || '';
      Promise.resolve(session.setAudioSource(selectedMicId)).catch((error) => {
        console.warn('[video-call] unable to switch MediTalk microphone device', error);
      });
    }
  }, [useLiveKit, liveKitStatus, selectedCameraId, selectedMicId]);

  const initializeMediaDevices = async () => {
    try {
      // Check if mediaDevices API is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        console.warn('Media devices API not available. This might be due to:');
        console.warn('1. Browser not supporting mediaDevices API');
        console.warn('2. Site not served over HTTPS (required for media access)');
        console.warn('3. Browser permissions not granted');
        setMicrophones([]);
        setCameras([]);
        return;
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const mics = devices.filter(device => device.kind === 'audioinput');
      const cams = devices.filter(device => device.kind === 'videoinput');
      
      setMicrophones(mics);
      setCameras(cams);
      
      if (mics.length > 0 && !selectedMicId) {
        setSelectedMicId(mics[0].deviceId);
      }
      if (cams.length > 0 && !selectedCameraId) {
        setSelectedCameraId(cams[0].deviceId);
      }
    } catch (error) {
      console.error('Error accessing media devices:', error);
      console.error('Media devices will not be available');
      setMicrophones([]);
      setCameras([]);
    }
  };

  const startLocalStream = async () => {
    try {
      // Check if getUserMedia is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        console.error('getUserMedia is not available. This might be due to:');
        console.error('1. Browser not supporting getUserMedia');
        console.error('2. Site not served over HTTPS (required for media access)');
        console.error('3. Browser permissions not granted');
        console.error('4. Camera/microphone not available');
        return;
      }

      const constraints = {
        video: isVideoOn ? { deviceId: selectedCameraId ? { exact: selectedCameraId } : undefined } : false,
        audio: isMicOn ? { deviceId: selectedMicId ? { exact: selectedMicId } : undefined } : false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      localStreamRef.current = stream;
      
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Error accessing media devices:', error);
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        constraint: error.constraint
      });
      
      // Show user-friendly error message
      if (error.name === 'NotAllowedError') {
        console.error('❌ Camera/microphone permission denied by user');
      } else if (error.name === 'NotFoundError') {
        console.error('❌ No camera/microphone found');
      } else if (error.name === 'NotReadableError') {
        console.error('❌ Camera/microphone is already in use');
      } else {
        console.error('❌ Unable to access camera/microphone');
      }
    }
  };

  const stopLocalStream = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = null;
    }
  };

  // Device changes use WebRTC's replaceTrack(). Do not start one after the
  // Vonage session has disconnected, because that connection no longer exists.
  const isVonageCallActive = () => {
    const session = vonageSessionRef.current;
    return Boolean(
      session &&
      (typeof session.isConnected !== 'function' || session.isConnected())
    );
  };

  const handleToggleMic = () => {
    const next = !isMicOn;
    setIsMicOn(next);
    if (livekitSessionRef.current) {
      livekitSessionRef.current.setAudioEnabled(next);
      return;
    }
    if (vonagePublisherRef.current && isVonageCallActive()) {
      vonagePublisherRef.current.publishAudio(next);
    } else if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach(track => {
        track.enabled = next;
      });
    }
  };

  const handleToggleVideo = () => {
    const next = !isVideoOn;
    setIsVideoOn(next);
    if (livekitSessionRef.current) {
      // Also covers the "started with no camera, plugged one in" case: the
      // adapter publishes a video track on demand when none exists yet.
      livekitSessionRef.current.setVideoEnabled(next, selectedCameraId);
      return;
    }
    if (vonagePublisherRef.current && isVonageCallActive()) {
      vonagePublisherRef.current.publishVideo(next);
    } else if (localStreamRef.current) {
      localStreamRef.current.getVideoTracks().forEach(track => {
        track.enabled = next;
      });
    } else if (!next) {
      startLocalStream();
    }
  };

  const handleSwitchCamera = async () => {
    // Generic camera switch handler for non-Vonage local streams
    if (!cameras || cameras.length <= 1) return;

    const currentIndex = cameras.findIndex(cam => cam.deviceId === selectedCameraId);
    const nextIndex = (currentIndex + 1) % cameras.length;
    const nextCamera = cameras[nextIndex] || cameras[0];

    if (!nextCamera) return;
    setSelectedCameraId(nextCamera.deviceId);
  };

  const handleSwitchCameraClick = async () => {
    // MediTalk (LiveKit): restart the published track on the next device.
    if (useLiveKit && livekitSessionRef.current) {
      if (!cameras || cameras.length < 2) return;
      try {
        const currentIndex = cameras.findIndex(cam => cam.deviceId === selectedCameraId);
        const nextIndex = (currentIndex + 1) % cameras.length;
        const nextCamera = cameras[nextIndex] || cameras[0];
        if (!nextCamera?.deviceId) return;
        await livekitSessionRef.current.setVideoSource(nextCamera.deviceId);
        setSelectedCameraId(nextCamera.deviceId);
      } catch (e) {
        console.warn('Unable to switch MediTalk camera:', e);
      }
      return;
    }

    const publisher = vonagePublisherRef.current;

    // When using Vonage, switch camera via Vonage publisher (don't gate on cameras.length – SDK may have access to more)
    if (useVonage && publisher && isVonageCallActive()) {
      try {
        // Prefer setVideoSource(deviceId) when we have multiple cameras for explicit front/back switch
        if (cameras && cameras.length >= 2 && typeof publisher.setVideoSource === 'function') {
          const currentIndex = cameras.findIndex(cam => cam.deviceId === selectedCameraId);
          const nextIndex = (currentIndex + 1) % cameras.length;
          const nextCamera = cameras[nextIndex] || cameras[0];
          if (nextCamera?.deviceId) {
            await publisher.setVideoSource(nextCamera.deviceId);
            setSelectedCameraId(nextCamera.deviceId);
            return;
          }
        }
        // Fallback: cycle through cameras (SDK handles device list internally)
        if (typeof publisher.cycleVideo === 'function') {
          const result = await publisher.cycleVideo();
          if (result?.deviceId) setSelectedCameraId(result.deviceId);
        }
      } catch (e) {
        console.warn('Unable to switch Vonage camera:', e);
      }
      return;
    }

    // Non-Vonage: switch via local stream and device list
    if (!cameras || cameras.length <= 1) return;
    handleSwitchCamera();
  };

  /** Capture call UI to a PNG blob. Optional callback(blob) for when done; if not provided, returns Promise<Blob | null>. */
  const captureScreenshotBlob = async (callback) => {
    const el = containerRef.current;
    if (!el) {
      if (typeof callback === 'function') callback(null);
      return null;
    }
    const rect = el.getBoundingClientRect();
    const w = Math.floor(rect.width);
    const h = Math.floor(rect.height);
    if (w <= 0 || h <= 0) {
      if (typeof callback === 'function') callback(null);
      return null;
    }
    const done = (blob) => {
      if (typeof callback === 'function') callback(blob);
      return blob;
    };
    try {
      const canvas = await html2canvas(el, {
        useCORS: true,
        allowTaint: true,
        logging: false,
        scale: 1,
        width: w,
        height: h,
        backgroundColor: '#1a1a1f'
      });
      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          const result = done(blob);
          resolve(result);
        }, 'image/png', 0.92);
      });
    } catch (err) {
      console.warn('[Screenshot] html2canvas failed, trying html-to-image:', err);
      try {
        const dataUrl = await htmlToImage.toPng(el, {
          width: w,
          height: h,
          backgroundColor: '#1a1a1f',
          pixelRatio: 1
        });
        const res = await fetch(dataUrl);
        const blob = await res.blob();
        return done(blob);
      } catch (err2) {
        console.warn('[Screenshot] html-to-image failed:', err2);
        if (typeof callback === 'function') callback(null);
        return null;
      }
    }
  };

  const handleScreenshot = async () => {
    const blob = await captureScreenshotBlob();
    if (!blob) return;
    if (screenshotFeedbackTimeoutRef.current) clearTimeout(screenshotFeedbackTimeoutRef.current);
    setScreenshotToastMessage('manual');
    screenshotFeedbackTimeoutRef.current = setTimeout(() => {
      setScreenshotToastMessage(null);
      screenshotFeedbackTimeoutRef.current = null;
    }, 2500);
    if (typeof onScreenshot === 'function') onScreenshot(blob);
  };

  const showAutoScreenshotToast = () => {
    if (screenshotFeedbackTimeoutRef.current) clearTimeout(screenshotFeedbackTimeoutRef.current);
    setScreenshotToastMessage('auto');
    screenshotFeedbackTimeoutRef.current = setTimeout(() => {
      setScreenshotToastMessage(null);
      screenshotFeedbackTimeoutRef.current = null;
    }, 2500);
  };

  // Auto-screenshot (pharmacist side only): first after 5s, then every 5s; saved to API and show toast
  useEffect(() => {
    if (!autoScreenshot || !isOpen || callState !== 'accepted') return;
    const onScreenshotFn = onScreenshotRef.current;
    if (typeof onScreenshotFn !== 'function') return;
    const AUTO_FIRST_MS = 5000;
    const AUTO_INTERVAL_MS = 5000;
    let timeoutId = null;
    let intervalId = null;
    const takeAutoScreenshot = () => {
      captureScreenshotBlob((blob) => {
        if (blob) {
          if (typeof onScreenshotRef.current === 'function') onScreenshotRef.current(blob);
          showAutoScreenshotToast();
        }
      });
    };
    timeoutId = setTimeout(() => {
      takeAutoScreenshot();
      intervalId = setInterval(takeAutoScreenshot, AUTO_INTERVAL_MS);
    }, AUTO_FIRST_MS);
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [autoScreenshot, isOpen, callState]);

  const handleEndCall = () => {
    if (livekitSessionRef.current) {
      try {
        livekitSessionRef.current.disconnect();
      } catch (e) {
        console.warn('LiveKit disconnect error:', e);
      }
      livekitSessionRef.current = null;
    }
    if (vonageSessionRef.current) {
      try {
        if (vonagePublisherRef.current) {
          vonageSessionRef.current.unpublish(vonagePublisherRef.current);
          vonagePublisherRef.current = null;
        }
        if (vonageSubscriberRef.current) {
          vonageSessionRef.current.unsubscribe(vonageSubscriberRef.current);
          vonageSubscriberRef.current = null;
        }
        vonageSessionRef.current.disconnect();
        vonageSessionRef.current = null;
      } catch (e) {
        console.warn('Vonage disconnect error:', e);
      }
    }
    stopLocalStream();
    setIsSettingsOpen(false);
    releaseScreenWakeLock();
    onClose();
  };

  const handleFullscreen = () => {
    if (!document.fullscreenElement && containerRef.current) {
      containerRef.current.requestFullscreen?.();
    } else if (document.fullscreenElement) {
      document.exitFullscreen?.();
    }
  };

  // Drag handlers for PiP
  const handlePipPointerDown = (e) => {
    if (pipContainerRef.current) {
      const rect = pipContainerRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      setIsDragging(true);
    }
  };

  useEffect(() => {
    if (!isDragging) return;

    const handlePointerMove = (e) => {
      if (pipContainerRef.current) {
        const container = pipContainerRef.current.parentElement;
        const containerRect = container.getBoundingClientRect();
        const pipRect = pipContainerRef.current.getBoundingClientRect();
        
        let newLeft = e.clientX - containerRect.left - dragOffset.x;
        let newTop = e.clientY - containerRect.top - dragOffset.y;
        
        // Constrain within container bounds
        newLeft = Math.max(0, Math.min(newLeft, containerRect.width - pipRect.width));
        newTop = Math.max(0, Math.min(newTop, containerRect.height - pipRect.height));
        
        setPipPosition({
          top: `${newTop}px`,
          left: `${newLeft}px`,
          right: 'auto'
        });
      }
    };

    const handlePointerUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);

    return () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
    };
  }, [isDragging, dragOffset]);

  // Keep screen on while ringing/connecting and during the active call.
  useEffect(() => {
    if (isOpen && (callState === 'accepted' || callState === 'requesting')) {
      requestScreenWakeLock();
      return () => {
        releaseScreenWakeLock();
      };
    }
  }, [isOpen, callState]);

  const isPharmacistToDriverCall = callMode === 'pharmacist_to_driver';
  const assistantDisplayName = driverName || (isPharmacistToDriverCall ? 'Assistant' : 'Driver');
  // Determine participant names based on caller/receiver role.
  const localParticipantName = isPharmacistToDriverCall ? pharmacistName : (isCaller ? assistantDisplayName : pharmacistName);
  const remoteParticipantName = isPharmacistToDriverCall ? assistantDisplayName : (isCaller ? pharmacistName : assistantDisplayName);

  const localParticipant = {
    id: 'local',
    name: localParticipantName,
    isLocal: true,
    isAudioOn: isMicOn,
    isVideoOn: isVideoOn
  };

  const remoteParticipant = {
    id: 'remote',
    name: remoteParticipantName,
    isLocal: false,
    isAudioOn: true,
    isVideoOn: false
  };

  // Debug: Log video layout
  // console.log('Video Call Layout:', {
  //   isCaller,
  //   localParticipant: localParticipantName,
  //   remoteParticipant: remoteParticipantName,
  //   localStream: !!localStreamRef.current,
  //   remoteStream: !!remoteStreamRef.current,
  //   layout: 'Local (PiP) | Remote (Main)'
  // });

  // Show different UI based on call state
  if (!isOpen) return null;

  // Show declined/ended state (sheet, consistent with other video call modals)
  if (callState === 'declined' || callState === 'ended') {
    const isEnded = callState === 'ended';
    const isBusy = !isEnded && statusReason === 'busy';
    return (
      <AnimatedBottomSheet isOpen onClose={onClose} ariaLabel="Video Call Status">
            <div className="bg-white pb-6 pt-2 px-6 relative rounded-t-[28px] overflow-hidden">
              {/* Handle + close */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex-1 flex justify-center pt-1">
                  <div className="w-10 h-0.5 rounded-full bg-gray-300" aria-hidden />
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="absolute right-6 top-3 p-1.5 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors -mt-0.5"
                  aria-label="Dismiss"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Icon + text */}
              <div className="flex flex-col items-center text-center mb-4">
                <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mb-3 shadow-sm">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">
                  {isEnded ? 'Call Ended' : isBusy ? 'Please Wait' : 'Call Declined'}
                </h3>
                <p className="text-sm text-gray-600">
                  {isEnded
                    ? 'No response was received. The call request expired.'
                    : isBusy
                      ? 'Please wait and hold. Pharmacist is on another call.'
                      : `${isCaller ? pharmacistName : assistantDisplayName} declined the video call request.`}
                </p>
              </div>

              {/* Action */}
              <button
                type="button"
                onClick={onClose}
                className="w-full inline-flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold text-sm py-2.5 px-4 rounded-xl transition-colors border border-gray-200"
              >
                Close
              </button>
            </div>
      </AnimatedBottomSheet>
    );
  }

  // Patient display name: lastname firstname. Pharmacist-to-driver calls are not patient based.
  const patientDisplayName = patientData?.last_name && patientData?.first_name
    ? `${(patientData.last_name || '').toString().toUpperCase()} ${(patientData.first_name || '').toString()}`
    : patientData?.first_name || (patientData?.last_name && patientData.last_name.toString()) || 'Patient';
  const patientPreferredName = (patientData?.preferred_name || '').toString().trim();
  const headerDisplayName = isPharmacistToDriverCall ? assistantDisplayName : patientDisplayName;
  const shouldShowPatientPreferredName = !isPharmacistToDriverCall && !isCaller && patientPreferredName;
  const callDate = callDateTime.datePart;
  const callTime = callDateTime.timePart;

  const isRequesting = callState === 'requesting';
  const requestingBadgeText = isPharmacistToDriverCall
    ? `Calling ${assistantDisplayName}`
    : `Calling ${pharmacistName}`;
  const connectingTitle = isPharmacistToDriverCall
    ? 'Connecting with assistant'
    : pharmacistBusy
      ? `${pharmacistName} is on another call`
      : 'Connecting Pharmacist Please wait';
  const connectingSubtitle = isPharmacistToDriverCall
    ? 'Waiting for the assistant to accept the video call.'
    : pharmacistBusy
      ? "You're in the queue — connecting you as soon as they're free."
      : '';

  // Show active or pre-connected call UI
  return (
    <div
      ref={screenshotAreaRef}
      className={`fixed inset-0 z-[100] flex justify-center items-center bg-[#0f0f12] ${!isCaller ? 'pharmacist-video-call' : ''}`}
    >
      {/* Screenshot saved feedback - top-level so it's never clipped, always visible */}
      {screenshotToastMessage && (
        <div
          className="fixed top-8 left-1/2 -translate-x-1/2 z-[110] flex items-center gap-2 px-5 py-3 rounded-full bg-emerald-600 text-white text-sm font-medium shadow-xl border border-emerald-500/50"
          role="status"
          aria-live="polite"
        >
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span>{screenshotToastMessage === 'auto' ? 'Auto captured' : 'Screenshot saved'}</span>
        </div>
      )}
      {useLiveKit && liveKitStatus === 'reconnecting' && (
        <div
          className="fixed top-20 left-1/2 z-[110] -translate-x-1/2 flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-500/90 px-4 py-2 text-sm font-medium text-amber-950 shadow-xl"
          role="status"
          aria-live="polite"
        >
          <span className="h-2 w-2 rounded-full bg-amber-900 animate-pulse" aria-hidden />
          Reconnecting — poor signal
        </div>
      )}
      {cameraPermissionState === 'denied' && (
        <div
          className="fixed top-6 left-1/2 z-[110] w-[min(92vw,460px)] -translate-x-1/2 rounded-xl border border-amber-300/60 bg-amber-50 px-4 py-3 text-sm text-amber-950 shadow-xl"
          role="alert"
        >
          <strong className="block">Camera permission is blocked.</strong>
          Allow camera access in your browser settings to share video. You can still continue the call with audio.
        </div>
      )}
      <div 
        ref={containerRef}
        className="w-full xl:w-3/12 lg:w-4/12 md:w-5/12 sm:w-6/12 h-full flex flex-col max-h-[100vh]"
        style={{
          backgroundColor: '#1a1a1f',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
          borderRadius: '16px'
        }}
      >
        {/* Main Video Area */}
        <div className="flex-1 flex relative min-h-0" style={{ overflow: 'hidden' }}>
          <div className="absolute top-4 left-4 z-20 flex flex-col gap-0.5">
            <p className={`font-bold text-lg leading-tight ${callState === 'accepted' ? 'text-black' : 'text-white'}`}>{headerDisplayName}</p>
            {shouldShowPatientPreferredName && (
              <p className={`text-xs ${callState === 'accepted' ? 'text-gray-600' : 'text-gray-300'}`}>
                Preferred Name: {patientPreferredName}
              </p>
            )}
            <p className="text-gray-400 text-sm flex items-center gap-2">
              <span>{callDate}</span>
              <span className="text-emerald-500 font-medium">{callTime}</span>
            </p>
            {isCaller && isRequesting && (
              <div className="mt-2 inline-flex w-fit items-center gap-2 rounded-full border border-blue-400/25 bg-blue-500/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-100">
                <span className="inline-flex gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-blue-300 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-blue-300 animate-bounce" style={{ animationDelay: '120ms' }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-blue-300 animate-bounce" style={{ animationDelay: '240ms' }} />
                </span>
                {requestingBadgeText}
              </div>
            )}
          </div>

          {/* Local Video (PiP) - top-right, vertical squircle, "YOU" inside at bottom */}
          <div 
            ref={pipContainerRef}
            className="absolute z-10 cursor-move flex flex-col"
            style={{ 
              ...pipPosition,
              width: '88px',
              height: '124px',
              maxWidth: 'calc(100% - 2rem)',
              userSelect: 'none',
              touchAction: 'none'
            }}
            onPointerDown={handlePipPointerDown}
          >
            <div 
              className="w-full h-full relative overflow-hidden flex flex-col rounded-[28px] border border-white/10 shadow-lg" 
              style={{ backgroundColor: '#25252a' }}
            >
              <div className="flex-1 min-h-0 relative">
                {useSdkContainers ? (
                  <div
                    ref={publisherContainerRef}
                    className="w-full h-full bg-[#1f1f24] rounded-[16px] overflow-hidden"
                    style={{ minHeight: 0 }}
                  />
                ) : (
                  <ParticipantTile
                    participant={localParticipant}
                    stream={localStreamRef.current}
                    className="w-full h-full"
                    showYouLabel={false}
                  />
                )}
              </div>
              <span className="absolute bottom-2 left-0 right-0 text-center text-[10px] font-medium uppercase tracking-wide text-gray-400">YOU</span>
            </div>
          </div>

          {/* Remote Video (Main) - Always shows other person's video in large container */}
          <div className="flex-1 flex items-center justify-center min-h-0" style={{ backgroundColor: '#0f0f12' }}>
            <div className="w-full h-full relative" style={{ backgroundColor: '#0f0f12' }}>
              {useSdkContainers ? (
                <div
                  ref={subscriberContainerRef}
                  className="w-full h-full bg-[#0f0f12] overflow-hidden"
                  style={{ minHeight: 0 }}
                />
              ) : (
                <ParticipantTile
                  participant={remoteParticipant}
                  isLarge={true}
                  stream={remoteStreamRef.current}
                  showLiveSession={true}
                  />
                )}
                {isRequesting && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-slate-950/20 via-slate-950/40 to-slate-950/65">
                    <div className="max-w-[82%] rounded-[24px] border border-white/10 bg-slate-950/70 px-5 py-4 text-center text-white shadow-[0_18px_40px_rgba(0,0,0,0.35)] backdrop-blur-md">
                      <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-600 text-lg font-bold text-white">
                        {(remoteParticipantName || 'D').trim().charAt(0).toUpperCase()}
                      </div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-200">{connectingTitle}</p>
                      {connectingSubtitle && (
                        <p className="mt-2 text-sm font-medium leading-snug text-slate-200">{connectingSubtitle}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

        {/* Controls - full width bar, same position for both participants */}
        <div 
          className="absolute bottom-5 left-4 right-4 flex justify-center"
          style={{ zIndex: 50 }}
        >
        <Controls
          isMicOn={isMicOn}
          isVideoOn={isVideoOn}
          onToggleMic={handleToggleMic}
          onToggleVideo={handleToggleVideo}
          onSwitchCamera={handleSwitchCameraClick}
          onScreenshot={handleScreenshot}
          onOpenSettings={() => setIsSettingsOpen(true)}
          onEndCall={handleEndCall}
          onFullscreen={handleFullscreen}
          minimalControls={!isCaller}
        />
      </div>

        {/* Settings Panel */}
        <SettingsPanel
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          microphones={microphones}
          cameras={cameras}
          selectedMicId={selectedMicId}
          selectedCameraId={selectedCameraId}
          onMicChange={setSelectedMicId}
          onCameraChange={setSelectedCameraId}
        />

        {/* Hidden video element for local stream */}
        <video
          ref={localVideoRef}
          autoPlay
          playsInline
          muted
          style={{ display: 'none' }}
        />
      </div>
    </div>
  );
};

export default VideoCall;
